import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { COMPANY, SERVICE_PAGES } from "@/data";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileServices, setMobileServices] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    setOpen(false);
    if (location.pathname === "/") {
      const el = document.querySelector(`#${id}`);
      if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
      else if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const linkCls =
    "font-body text-[15px] font-medium text-navy/80 hover:text-gold transition-colors duration-200 relative group";
  const underline =
    "absolute -bottom-1 left-0 h-[2px] w-0 bg-gold transition-[width] duration-300 group-hover:w-full";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "glass bg-white/90 border-b border-navy/10 shadow-[0_4px_30px_rgba(12,30,68,0.06)]"
          : "bg-white/60 glass border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-5 md:px-12 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="logo-link" aria-label="Almeida's Home Services home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {/* Services dropdown */}
          <div className="relative group">
            <button className={`${linkCls} flex items-center gap-1`} data-testid="nav-services">
              Services <ChevronDown size={15} />
              <span className={underline} />
            </button>
            <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
              <div className="w-72 bg-white rounded-2xl border border-navy/10 shadow-[0_20px_50px_rgba(12,30,68,0.12)] p-2">
                {SERVICE_PAGES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    data-testid={`nav-service-${s.slug}`}
                    className="block px-4 py-2.5 rounded-xl font-body text-[14px] text-navy/80 hover:bg-cloud hover:text-navy transition-colors duration-150"
                  >
                    {s.nav}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link to="/service-areas" className={linkCls} data-testid="nav-service-areas">
            Service Areas <span className={underline} />
          </Link>
          <button onClick={() => goSection("portfolio")} className={linkCls} data-testid="nav-portfolio">
            Portfolio <span className={underline} />
          </button>
          <button onClick={() => goSection("about")} className={linkCls} data-testid="nav-about">
            About <span className={underline} />
          </button>
          <button onClick={() => goSection("testimonials")} className={linkCls} data-testid="nav-testimonials">
            Testimonials <span className={underline} />
          </button>
          <Link to="/contact" className={linkCls} data-testid="nav-contact">
            Contact <span className={underline} />
          </Link>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="header-call-link"
            className="flex items-center gap-2 text-navy font-semibold text-[15px] hover:text-gold transition-colors duration-200"
          >
            <Phone size={17} strokeWidth={2.4} />
            {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            data-testid="header-estimate-btn"
            className="rounded-full bg-gold hover:bg-gold-hover text-navy-dark font-bold text-[14px] uppercase tracking-wide px-6 py-3 transition-colors duration-200"
          >
            Free Estimate
          </Link>
        </div>

        <button
          className="lg:hidden text-navy p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-navy/10 px-5 py-5 flex flex-col gap-1 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <button
            onClick={() => setMobileServices((s) => !s)}
            className="flex items-center justify-between py-3 font-body font-medium text-navy border-b border-navy/5"
          >
            Services <ChevronDown size={18} className={mobileServices ? "rotate-180 transition-transform" : "transition-transform"} />
          </button>
          {mobileServices &&
            SERVICE_PAGES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                onClick={() => setOpen(false)}
                className="py-2.5 pl-4 font-body text-sm text-navy/75 border-b border-navy/5"
              >
                {s.nav}
              </Link>
            ))}
          <Link to="/service-areas" onClick={() => setOpen(false)} className="py-3 font-body font-medium text-navy border-b border-navy/5">
            Service Areas
          </Link>
          <button onClick={() => goSection("portfolio")} className="py-3 text-left font-body font-medium text-navy border-b border-navy/5">
            Portfolio
          </button>
          <button onClick={() => goSection("about")} className="py-3 text-left font-body font-medium text-navy border-b border-navy/5">
            About
          </button>
          <button onClick={() => goSection("testimonials")} className="py-3 text-left font-body font-medium text-navy border-b border-navy/5">
            Testimonials
          </button>
          <Link to="/contact" onClick={() => setOpen(false)} className="py-3 font-body font-medium text-navy border-b border-navy/5">
            Contact
          </Link>
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            className="mt-3 flex items-center justify-center gap-2 rounded-full border-2 border-navy text-navy font-semibold py-3"
            data-testid="mobile-call-link"
          >
            <Phone size={17} /> {COMPANY.phone}
          </a>
          <Link
            to="/contact"
            onClick={() => setOpen(false)}
            className="mt-2 rounded-full bg-gold text-navy-dark font-bold uppercase tracking-wide text-center py-3.5"
            data-testid="mobile-estimate-btn"
          >
            Free Estimate
          </Link>
        </div>
      )}
    </header>
  );
};

export default Header;
